import { Button, Link, Stack, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { errorMessageFor } from 'utils/forms.utils';
import { handleFocusOtp } from 'utils/mui-one-time-code.utils';

import {
  SAUDI_REWARDS_FORGOT_PIN_URL,
  SAUDI_REWARDS_URL,
} from 'constants/saudi-rewards.constants';
import { VALIDATIONS } from 'constants/validation.constants';
import { useLinkSaudiRewardsAccount } from 'queries/accounts/useLinkSaudiRewardsAccount';

import { SaudiRewardsFormFields } from 'components/@account/types';
import Form from 'components/@forms/Form';
import FormOTPField from 'components/@forms/FormOTPField';
import FormTextField from 'components/@forms/FormTextField';
import { SubmitHandler } from 'components/@forms/types';
import StickyButtonContainer from 'components/@saudi-rewards/SaudiRewardsRetroRedeemContainer/StickyButtonContainer';
import { StepData } from 'components/@saudi-rewards/SaudiRewardsRetroRedeemContainer/types';
import { useMultiStep } from 'components/@step-flow/MultiStep';

const LinkAccount = () => {
  const intl = useIntl();
  const multistep = useMultiStep<StepData>();
  const { linkSaudiRewardsAccount, isLoading } = useLinkSaudiRewardsAccount();
  const { enqueueSnackbar } = useSnackbar();

  const handleSubmit: SubmitHandler<SaudiRewardsFormFields> = (data) => {
    linkSaudiRewardsAccount(
      {
        memberId: data.saudiRewardsMemberId,
        memberPin: data.saudiRewardsMemberPin,
      },
      {
        onSuccess: () => {
          enqueueSnackbar(
            intl.formatMessage({ id: 'saudi.rewards.link.success.alert' }),
            { variant: 'success' },
          );
          multistep.proceed();
        },
        onError: () => {
          enqueueSnackbar(
            intl.formatMessage({ id: 'api.error.saudi.rewards' }),
            { variant: 'error' },
          );
        },
      },
    );
  };

  return (
    <Stack sx={{ flexGrow: 1 }}>
      <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
        <FormattedMessage id="saudi.rewards.link.account.title" />
      </Typography>
      <Typography variant="small" sx={{ mb: 2 }}>
        <FormattedMessage
          id="saudi.rewards.link.form.title"
          values={{
            a: (chunks: Array<ReactNode>) => {
              return (
                <Link
                  href={SAUDI_REWARDS_URL}
                  target="_blank"
                  underline="always"
                  variant="small"
                  color="inherit"
                >
                  {chunks}
                </Link>
              );
            },
          }}
        />
      </Typography>
      <Form
        onSubmit={handleSubmit}
        defaultValues={multistep.values}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: { xs: 1, md: 0 },
        }}
      >
        <Stack
          sx={{
            gap: 1,
            mb: 2,
            flexGrow: 1,
          }}
        >
          <FormTextField
            label={<FormattedMessage id="form.label.member_id" />}
            name="saudiRewardsMemberId"
            rules={{
              required: errorMessageFor(intl, {
                type: 'required',
                label: 'member_id',
              }),
              pattern: {
                value: VALIDATIONS.saudiRewardsMemberId,
                message: intl.formatMessage({
                  id: 'saudi.rewards.validate.pin.error',
                }),
              },
            }}
            type="text"
            inputMode="text"
            autoFocus
            onKeyDown={handleFocusOtp}
          />

          <FormOTPField
            name="saudiRewardsMemberPin"
            rules={{
              validate: (value) =>
                value.length === 4
                  ? true
                  : errorMessageFor(intl, {
                      type: 'min_length',
                      label: 'pin',
                      value: 4,
                    }),
              required: errorMessageFor(intl, {
                type: 'required',
                label: 'pin',
              }),
            }}
            type="password"
            codeLength={4}
          />

          <Link
            href={SAUDI_REWARDS_FORGOT_PIN_URL}
            target="_blank"
            variant="small"
            underline="always"
            color="text.secondary"
          >
            <FormattedMessage id="saudi.rewards.link.form.forgot.pin" />
          </Link>
        </Stack>
        <StickyButtonContainer>
          <Button
            variant="outlined"
            color="secondary"
            type="submit"
            loading={isLoading}
            sx={{ width: { xs: '100%', md: 'fit-content' } }}
          >
            <FormattedMessage id="saudi.rewards.link.account.button" />
          </Button>
        </StickyButtonContainer>
      </Form>
    </Stack>
  );
};

export default LinkAccount;
